import React, { Fragment, useState, useEffect } from 'react';
import { Form, message, Radio, Descriptions, Tag, Card, Typography } from 'antd';
import moment from 'moment';
import { updateOrganizationBackupsConfig } from '../api';

function OrganizationBackups(props: any) {
	const { organization } = props;
	const [saving, setSaving] = useState(false);
	const [config, setConfig] = useState(organization?.organization_backups || {});

	useEffect(() => {
		setConfig(organization?.organization_backups || {});
	}, [organization]);

	async function onUpdateConfig(payload: any) {
		try {
			setSaving(true);
			const updated = await updateOrganizationBackupsConfig({ organization_id: organization.id, ...payload });

			setConfig((prevConfig: any) => ({ ...prevConfig, ...updated }));
			setSaving(false);
		} catch (err) {
			setSaving(false);
			message.error('Unable to update organization backups. Something went wrong.');
		}
	}

	function getStatusText(status: string) {
		switch (status) {
			case 'done':
			case 'idle':
				return null;
			case 'processing':
				return <Tag color="blue">Generating new backup...</Tag>;
			case 'error':
				return <Tag color="red">Backup generation failed. Will be retried automatically.</Tag>;
			default:
				return status;
		}
	}

	const lastBackup = config.last_backup_at ? moment(config.last_backup_at).format('DD.MM.YYYY') : null;

	const nextBackup = (() => {
		if (!config.enabled) {
			return 'Disabled';
		}
		if (!config.last_backup_at || !config.frequency_days) {
			return moment().format('DD.MM.YYYY');
		}

		const nextDate = moment(config.last_backup_at);
		nextDate.add(Number(config.frequency_days), 'days');

		return nextDate.format('DD.MM.YYYY');
	})();

	const formatBytes = (bytes: number) => {
		if (!bytes && bytes !== 0) {
			return '-';
		}

		if (bytes === 0) {
			return '0 B';
		}

		const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
		const i = Math.floor(Math.log(bytes) / Math.log(1024));
		const value = bytes / (1024 ** i);

		return `${value.toFixed(1)} ${sizes[i]}`;
	};

	return (
		<div>
			<Form.Item label="Backup frequency (days)">
				<Radio.Group
					defaultValue={7}
					value={config.enabled ? config.frequency_days : null}
					buttonStyle="solid"
					onChange={(e) =>
						onUpdateConfig({ frequency_days: e.target.value, enabled: e.target.value !== null })
					}
				>
					<Radio.Button value={null}>Disabled</Radio.Button>
					<Radio.Button value={1}>1 day</Radio.Button>
					<Radio.Button value={7}>7 days</Radio.Button>
					<Radio.Button value={14}>14 days</Radio.Button>
					<Radio.Button value={30}>30 days</Radio.Button>
				</Radio.Group>
			</Form.Item>
			<Descriptions style={{ marginBottom: 24 }} size="small" column={1}>
				<Descriptions.Item label="Next backup">
					{nextBackup}
					<span style={{ marginLeft: 8 }}></span>
				</Descriptions.Item>
				{getStatusText(config.status) && <Descriptions.Item>{getStatusText(config.status)}</Descriptions.Item>}
			</Descriptions>
			<Card style={{ marginBottom: 24 }} title="Latest backup">
				{!lastBackup && 'No backups have been completed yet.'}
				{lastBackup && (
					<Descriptions size="small" column={1}>
						<Descriptions.Item label="Date">
							<Typography.Text>{lastBackup}</Typography.Text>
						</Descriptions.Item>
						<Fragment>
							<Descriptions.Item label="Size">
								<Typography.Text>{formatBytes(config.backup_size)}</Typography.Text>
							</Descriptions.Item>
						</Fragment>
					</Descriptions>
				)}
			</Card>
		</div>
	);
}

export default OrganizationBackups;
